import type { ReactNode } from "react";
import posthog from "posthog-js";
import { PostHogErrorBoundary, PostHogProvider } from "@posthog/react";
import { env } from "@/env";

let initialized = false;

export function TelemetryRoot({ children }: { children: ReactNode }) {
  if (!isPostHogEnabled()) return <>{children}</>;
  initPostHog();

  return (
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary fallback={<TelemetryFallback />}>
        {children}
      </PostHogErrorBoundary>
    </PostHogProvider>
  );
}

export function identifyTelemetryUser(user: {
  id: string;
  email?: string | null;
}) {
  if (!initialized) return;
  posthog.identify(user.id, {
    email_domain: emailDomain(user.email),
    env: env.APP_ENV,
    release: env.PARABLE_RELEASE_SHA,
  });
  posthog.group("workspace", "ws_default");
}

export function captureTelemetryEvent(
  event: string,
  properties?: Record<string, unknown>,
) {
  if (!initialized) return;
  posthog.capture(event, properties);
}

export function resetTelemetryUser() {
  if (!initialized) return;
  posthog.reset();
}

function initPostHog() {
  if (initialized) return;
  initialized = true;
  posthog.init(env.PUBLIC_POSTHOG_PROJECT_TOKEN!, {
    api_host: env.PUBLIC_POSTHOG_HOST,
    defaults: "2026-01-30",
    person_profiles: "identified_only",
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
    error_tracking: {
      captureExtensionExceptions: false,
    },
    loaded: (client) => {
      client.register({
        env: env.APP_ENV,
        service: "web",
        runtime: "browser",
        release: env.PARABLE_RELEASE_SHA,
      });
    },
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
      maskTextSelector: ".ph-no-capture,.ph-mask",
      blockSelector: ".ph-no-capture",
    },
  });
}

function isPostHogEnabled(): boolean {
  if (!env.PUBLIC_POSTHOG_PROJECT_TOKEN) return false;
  if (env.TELEMETRY_DISABLED) return false;
  return env.APP_ENV === "production" || env.TELEMETRY_DEV;
}

function emailDomain(email: string | null | undefined): string | null {
  const idx = email?.lastIndexOf("@") ?? -1;
  return idx >= 0 ? email!.slice(idx + 1).toLowerCase() : null;
}

function TelemetryFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-wiki-bg px-6 text-center text-sm text-wiki-fg-3">
      Something went wrong. Please restart Parable and try again.
    </div>
  );
}
